(ns app.front.view.group.admin.users.core
  "Group user management: users, service accounts and email recipients on their
  own tabs, edited in the shared user editor and saved with Save."
  (:require ["@carbon/pictograms-react/es/single--sign-on" :default SingleSignOn]
            [app.domain.group.model :as group-model]
            [app.domain.util :as du]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.ui.tabs :as ui-tabs]
            [app.front.ui.yes-no-radio-buttons :as yes-no]
            [app.front.util :refer [for-frag]]
            [app.front.validate :as validate]
            [app.front.view.group.admin.add-user :as add-user]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.mfa.core :as group.admin.mfa]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.admin.users.service-accounts :as service-accounts]
            [app.front.view.group.admin.users.subs :as subs]
            [app.front.view.profile.api-keys :as profile-api-keys]
            [app.front.view.user-management.components :as um]
            [app.front.view.user-management.model :as model]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

;; ---------------------------------------------------------------------------
;; Group choices
;; ---------------------------------------------------------------------------

(defn- has-role-of? [role-set {:group/keys [roles]}]
  (-> (set roles)
      (set/intersection role-set)
      seq
      boolean))

(defn- superdomain-blocks
  "A superdomain's groups by the level they act on."
  [groups]
  (->> [[:user-management/superdomain-user-groups group-model/superdomain-group-child-roles]
        [:user-management/domain-user-groups group-model/domain-group-child-roles]
        [:user-management/authority-user-groups group-model/authority-group-child-roles]]
       (reduce (fn [[blocks left] [title roles]]
                 (let [{in true out false} (group-by (partial has-role-of? roles) left)]
                   [(conj blocks [title in]) out]))
               [[] groups])
       first))

(defn- qualification-name [tr lang {:group/keys [name] :keys [qualification-names]}]
  (or (i18n-model/get-text qualification-names lang)
      (tr [(keyword "qualification.type" name)])))

(defn- groups-editor
  "The group's user-groups as the legacy view lays them out: an external
  module's roles and departments; a superdomain's permissions and its groups
  by level; otherwise a viewer-only choice that hides the rest. The inspectors
  group brings its qualifications."
  [{:keys [tr id-prefix group groups qualifications superdomain? session-user? admin-group-ids
           member-counts inspectors-group-id lang]}]
  (fn [selected on-toggle]
    (let [{:keys [permissions viewer plain]} (model/classify-groups groups)
          viewer-group                       (first viewer)
          viewer-only?                       (and viewer-group (contains? selected (:db/id viewer-group)))
          disabled?                          (partial model/group-disabled?
                                                      {:own-admin-group-ids (when session-user? admin-group-ids)
                                                       :member-counts       member-counts}
                                                      groups
                                                      selected)
          block                              (fn [suffix title block-groups]
                                               [um/checkbox-block {:id-prefix (str id-prefix suffix)
                                                                   :title     title
                                                                   :groups    block-groups
                                                                   :selected  selected
                                                                   :on-toggle on-toggle
                                                                   :disabled? disabled?}])]
      [:<>
       (cond
         (group-model/external-module? group)
         [um/group-checkboxes {:tr          tr
                               :id-prefix   id-prefix
                               :module-name (:group/name group)
                               :groups      groups
                               :selected    selected
                               :on-toggle   on-toggle}]

         superdomain?
         [:<>
          (block "-perm" (tr [:user-management/permissions])
                 (concat (map #(assoc % :group/name (tr [:user-management/view-permission])) viewer)
                         permissions))
          (for-frag [[title block-groups] (superdomain-blocks plain)]
            (block (str "-" (name title)) (tr [title]) block-groups))]

         :else
         [:<>
          ;; An admin cannot make themselves a viewer only.
          (when (and viewer-group (not session-user?))
            [:div.mt-4
             [:h5.mb-1 (tr [:user-management/viewer-only])]
             [yes-no/yes-no-radio-buttons tr (str id-prefix "-viewer-only")
              {:value-selected (boolean viewer-only?)
               :on-change      #(on-toggle (:db/id viewer-group) %)}]])
          (when-not viewer-only?
            [:<>
             (block "-perm" (tr [:user-management/permissions]) permissions)
             (block "-groups" (tr [:user-management/groups]) plain)])])
       (when (contains? selected inspectors-group-id)
         [um/checkbox-block {:id-prefix (str id-prefix "-qualifications")
                             :title     (tr [:user-management/qualifications])
                             :groups    (map #(assoc % :group/name (qualification-name tr lang %)) qualifications)
                             :selected  selected
                             :on-toggle on-toggle}])])))

;; ---------------------------------------------------------------------------
;; Users tab
;; ---------------------------------------------------------------------------

(defn- mfa-button [{:keys [tr group-id user]}]
  (when-let [config (first (<sub [::group.admin.mfa/mfa-configs]))]
    (let [user-mfa (:user/mfa-config user)
          enabled? (= (:mfa-config/id user-mfa) (:mfa-config/id config))]
      (if (or enabled? (not user-mfa))
        [ui/button {:kind     :tertiary
                    :size     :small
                    :icon     (if enabled? i/lock-open i/lock)
                    :on-click #(>evt [::subs/set-user-mfa-status group-id
                                      {:mfa-config/id (:mfa-config/id config)
                                       :user-id       (:db/id user)
                                       :enabled?      (not enabled?)}])}
         (tr [(if enabled? :user-management/disable-mfa :user-management/enable-mfa)]
             [(:mfa-config/display-name config)])]
        [:span.text-sm (tr [:user-management/other-mfa-enabled] [(:mfa-config/display-name user-mfa)])]))))

(defn- remove-button [{:keys [tr group-id user title]}]
  (let [self?  (= (:db/id user) (:db/id (<sub [:session/user])))
        admin? (<sub [:session/admin?])]
    [ui/modal-confirmation-button
     {:tr                  tr
      :modal-title         title
      :trigger-button-text title
      :trigger-button-opts {:size :small :kind :danger :icon i/remove}
      ;; Group admins do not remove themselves.
      :disabled?           (and self? (not admin?))
      :on-yes-click        #(>evt [::subs/remove-user group-id (:db/id user)])}
     (tr [:user-management/remove-from-group-confirmation])]))

(defn- editor-context
  "What the groups editor needs about the group and the user being edited."
  [tr group-id user]
  {:tr                  tr
   :id-prefix           (str "gu-" (:db/id user))
   :group               (<sub [::gas/group])
   :groups              (<sub [::subs/user-groups group-id])
   :qualifications      (<sub [::subs/qualifications group-id])
   :superdomain?        (<sub [::gas/superdomain-group?])
   :session-user?       (= (:db/id user) (:db/id (<sub [:session/user])))
   :admin-group-ids     (<sub [::subs/admin-group-ids group-id])
   :member-counts       (<sub [::subs/member-counts group-id])
   :inspectors-group-id (<sub [::subs/inspectors-group-id group-id])
   :lang                (<sub [:language])})

(defn- toggle-fn [{:keys [groups qualifications superdomain? inspectors-group-id]}]
  (partial subs/toggle-group {:groups              groups
                              :superdomain?        superdomain?
                              :inspectors-group-id inspectors-group-id
                              :qualification-ids   (du/entity-id-set qualifications)}))

(defn- person-editor [tr group-id user]
  (let [{:keys [inspectors-group-id] :as context} (editor-context tr group-id user)
        approver-group?                           (<sub [::subs/approver-group? group-id])
        assignment-group-names                    (model/review-assignment-group-names user)]
    [um/user-editor
     {:tr            tr
      :scope         (subs/scope group-id)
      :user          user
      :id-prefix     "gu"
      :editable-ids  (<sub [::subs/editable-ids group-id])
      :toggle-fn     (toggle-fn context)
      :groups-editor (groups-editor context)
      ;; An approver group's members are only managed as members.
      :names?        (not approver-group?)
      :save-event    [::subs/save-user group-id]
      :confirm-save  (fn [{:keys [remove]}]
                       (when (contains? remove inspectors-group-id)
                         (tr [:user-management/inspectors-remove-confirmation] [(model/display-name user)])))
      :extra         (when-not approver-group?
                       [:div.flex.flex-wrap.items-center.gap-4.mt-4
                        (when assignment-group-names
                          [:div.mr-auto
                           [:div.text-sm.text-gray-600 (tr [:review-assignment-group/belongs-to])]
                           [:div (str/join ", " assignment-group-names)]])
                        [mfa-button {:tr tr :group-id group-id :user user}]])
      :remove-button [remove-button {:tr       tr
                                     :group-id group-id
                                     :user     user
                                     :title    (tr [:user-management/remove-account])}]}]))

(defn- sso-cell [tr user]
  (when (:user/oidc-config user)
    [:span {:title (tr [:user-management/sso-enabled])}
     [:> SingleSignOn {:title  (tr [:user-management/sso-enabled])
                       :width  24
                       :height 24
                       :fill   "#4F0891"}]]))

(defn- group-names [group-id]
  (let [names-by-id (<sub [::subs/group-names-by-id group-id])]
    #(keep names-by-id (:user/group-ids %))))

(defn- sync-pending
  "An external module's user-groups not yet pushed to its system, with a retry."
  [tr group-id]
  (when-let [{:keys [module-name group-names]} (<sub [::subs/sync-pending group-id])]
    [:div.flex.items-center.gap-3.flex-wrap
     (ui/warning {:class "flex-1"} (tr [:admin/tenant-sync-pending] [(str/join ", " group-names) module-name]))
     [ui/button {:kind     :secondary
                 :size     :small
                 :on-click #(>evt [::subs/retry-sync group-id])}
      (tr [:admin/tenant-retry-sync])]]))

(defn- users-tab [tr group-id]
  (let [scope    (subs/scope group-id)
        users    (<sub [::subs/persons group-id])
        loading? (<sub [::subs/loading? group-id])]
    [acommon/section
     {}
     [:div.flex.items-start.gap-4.flex-wrap
      [:div.flex-1
       [um/search-field {:tr    tr
                         :scope scope
                         :id    "group-users-search"
                         :label (tr [:admin/tenant-user-search-label])
                         :light false}]]
      ;; The legacy view's add form, as it was.
      [ui/button {:kind     :primary
                  :size     :default
                  :icon     i/user-plus
                  :data-cy  "add-new-user"
                  :on-click (add-user/handle-open group-id
                                                  (<sub [::subs/user-groups group-id])
                                                  [::subs/added group-id])}
       (tr [:admin/add-user])]]
     [sync-pending tr group-id]
     (cond
       (and loading? (empty? users))
       (cc/inline-loading {:description (tr [:loading])})

       (empty? users)
       [:p.text-gray-500 (tr [:user-management/no-users-found])]

       :else
       [um/persons-table {:tr            tr
                          :id            "group-users-table"
                          :users         users
                          :group-names   (group-names group-id)
                          :expanded-func (partial person-editor tr group-id)
                          :extra-cols    [["" (partial sso-cell tr) {:width "64px"}]]}])
     [add-user/modal tr]]))

;; ---------------------------------------------------------------------------
;; Service accounts tab
;; ---------------------------------------------------------------------------

(defn- api-keys [tr group-id user]
  [:div.mt-4.border.border-ui-gray.border-solid.rounded.p-4
   [:h4.mb-2 (tr [:api-key-ui/section-title])]
   (if-let [keys (seq (:user/api-keys user))]
     [profile-api-keys/api-key-table {:tr                 tr
                                      :api-keys           keys
                                      :update-success-evt [::subs/api-key-updated group-id (:db/id user)]
                                      :delete-success-evt [::subs/api-key-deleted group-id (:db/id user)]}]
     [:p (tr [:group-admin.service-accounts/no-api-keys])])
   [:div.mt-4.flex.justify-end
    [ui/button {:kind     :tertiary
                :size     :small
                :icon     i/plus
                :on-click #(>evt [::subs/create-api-key group-id (:db/id user)])}
     (tr [:api-key-ui/add-new])]]])

(defn- service-account-editor
  "The same group choices as a person's, as in the legacy view, without names."
  [tr group-id user]
  (let [context (editor-context tr group-id user)]
    [um/user-editor
     {:tr            tr
      :scope         (subs/scope group-id)
      :user          user
      :id-prefix     "gsa"
      :editable-ids  (<sub [::subs/editable-ids group-id])
      :toggle-fn     (toggle-fn context)
      :groups-editor (groups-editor context)
      :names?        false
      :save-event    [::subs/save-user group-id]
      :extra         [api-keys tr group-id user]
      :remove-button [remove-button {:tr       tr
                                     :group-id group-id
                                     :user     user
                                     :title    (tr [:group-admin.service-accounts/remove-account])}]}]))

(defn- service-accounts-tab [tr group-id]
  (let [scope    (subs/scope group-id)
        group    (<sub [::gas/group])
        accounts (<sub [::subs/service-accounts group-id])]
    [acommon/section
     {}
     [:p.mb-4 (tr [:group-admin.service-accounts/section-guide])]
     [:div.flex.items-start.gap-4.flex-wrap
      [:div.flex-1
       [um/search-field {:tr    tr
                         :scope scope
                         :id    "group-service-accounts-search"
                         :label (tr [:admin/tenant-service-account-search-label])
                         :light false}]]
      ;; The legacy view's add form, as it was.
      [ui/button {:kind     :primary
                  :size     :default
                  :icon     i/plus
                  :on-click #(>evt [::service-accounts/open-modal group-id
                                    (<sub [::subs/user-groups group-id])
                                    [::subs/fetch-users group-id]])}
       (tr [:group-admin.service-accounts/add-new])]]
     (if (seq accounts)
       [um/service-accounts-table {:tr            tr
                                   :id            "group-service-accounts-table"
                                   :users         accounts
                                   :group-names   (group-names group-id)
                                   :expanded-func (partial service-account-editor tr group-id)}]
       [:p.text-gray-500 (tr [:admin/tenant-no-service-accounts-in-group])])
     [service-accounts/add-account-modal {:tr tr :group group}]]))

;; ---------------------------------------------------------------------------
;; Email recipients tab
;; ---------------------------------------------------------------------------

(defn parse-emails
  "Addresses in `text`, separated by whitespace, commas or semicolons, as
  `{:emails [...]}`, or `{:error ...}` naming the first invalid one. Nil when
  `text` is blank."
  [tr text]
  (when-not (str/blank? text)
    (let [emails (str/split (str/trim text) #"[\s,;]+")]
      (if-let [bad (p/find-first emails validate/check-email)]
        {:error (tr [:user-management/email-error] [bad])}
        {:emails emails}))))

(defn- add-emails-modal [tr group-id email-group]
  (let [text                   (<sub [::subs/emails-form group-id])
        {:keys [error emails]} (parse-emails tr text)
        close                  #(>evt [::subs/close-emails-form group-id])]
    [ui-modal/composed-input-modal
     {:open                   (some? text)
      :selector-primary-focus "#email-recipients-textarea"
      :on-close               close}
     (cc/modal-header {:close-modal close :title (tr [:user-management/email-add])})
     (when text
       (cc/modal-body
         {}
         [ui/text-area {:id           "email-recipients-textarea"
                        :label-text   (tr [:user-management/email-add-label])
                        :rows         10
                        :value        text
                        :invalid      (boolean error)
                        :invalid-text error
                        :on-change    #(rf/dispatch-sync [::subs/set-emails-text group-id (.. % -target -value)])}]))
     (cc/modal-footer
       {}
       (cc/button {:kind :secondary :on-click close} (tr [:cancel]))
       (cc/button {:kind     :primary
                   :disabled (not emails)
                   :on-click #(>evt [::subs/edit-emails group-id (:db/id email-group) :add emails])}
                  (tr [:save])))]))

(defn- email-recipients-tab [tr group-id]
  (let [email-group (<sub [::subs/email-recipients-group group-id])]
    [acommon/section
     {}
     [:p.mb-4 (tr [:user-management/email-info])]
     [:div.flex.justify-end.mb-4
      [ui/button {:kind     :primary
                  :size     :small
                  :icon     i/plus
                  :on-click #(>evt [::subs/open-emails-form group-id])}
       (tr [:user-management/email-add])]]
     (if-let [emails (seq (:notification/emails email-group))]
       [sortable-table/table
        {:tr              tr
         :id              "group-email-recipients-table"
         :items           emails
         :id-func         identity
         :initial-sorting [::email :asc]
         :cols            [[:email-address identity {:sorting true :sort-key ::email}]]
         :make-actions    (fn [email]
                            [{:title           (tr [:user-management/email-remove])
                              :icon            i/remove
                              :confirm?        true
                              :confirm-content (tr [:user-management/email-remove-confirmation] [email])
                              :on-click        #(>evt [::subs/edit-emails group-id (:db/id email-group) :remove [email]])}])}]
       [:p.text-gray-500 (tr [:none])])
     [add-emails-modal tr group-id email-group]]))

;; ---------------------------------------------------------------------------
;; Page
;; ---------------------------------------------------------------------------

(def ^:private tab-ids ["users" "service-accounts" "email-recipients"])

(defn- tabs
  "The tabs this group has: service accounts need API keys and are for people
  only in an external module; email recipients need their group."
  [tr group-id]
  (let [group (<sub [::gas/group])]
    (cond-> [["users" (tr [:admin/users]) users-tab]]
      (and (group-model/use-api-keys? group)
           (not (group-model/external-module? group)))
      (conj ["service-accounts" (tr [:admin/tenant-service-accounts]) service-accounts-tab])

      (<sub [::subs/email-recipients-group group-id])
      (conj ["email-recipients" (tr [:user-management/email-form-title]) email-recipients-tab]))))

(defn- tab-href [group-id tab]
  (href/href :view/group.admin.users {:group-id group-id} (when (not= "users" tab) {:tab tab})))

(defn- loaded-view [tr match group-id]
  (let [tabs                     (tabs tr group-id)
        requested                (-> match :parameters :query :tab)
        [selected label content] (or (p/find-first tabs #(= requested (first %)))
                                     (first tabs))]
    [acommon/admin-page-v2
     {:match          match
      :title          (tr [:user-management/title])
      :header-content (when (next tabs)
                        [ui-tabs/tabs
                         {:selected-id selected
                          :aria-label  (tr [:user-management/title])
                          :tabs        (map (fn [[id label]]
                                              {:id id :label label :href (tab-href group-id id)})
                                            tabs)}])}
     [acommon/section-title-wrapper {:title label}
      [content tr group-id]]]))

(defn view [match]
  (let [tr       (<sub [::gas/group-tr])
        group-id (-> match :parameters :path :group-id)]
    ;; The tabs, the editors and the add forms all need the user-groups.
    (if (<sub [::subs/groups-loaded? group-id])
      [loaded-view tr match group-id]
      [acommon/admin-page-v2
       {:match match
        :title (tr [:user-management/title])}
       [acommon/section-title-wrapper {:title (tr [:admin/users])}
        [acommon/section {}
         (cc/data-table-skeleton)]]])))

(def route
  {:name        :view/group.admin.users
   :view        view
   :view-sub    [::subs/view-data]
   :public?     false
   :own-layout? true
   :parameters  {:path  {:group-id s/Int}
                 :query {(s/optional-key :tab) (apply s/enum tab-ids)}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [{{:keys [group-id]} :path}]
                                (>evt [::subs/fetch group-id])
                                (>evt [::group.admin.mfa/fetch-mfa-configs group-id]))}]})
